import React, { useState, useCallback, useMemo } from "react";
import { ExternalLink, TrendingUp, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "@/packages/utils";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
  Customized,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#ef4444",
  major: "#f59e0b",
  minor: "#3b82f6",
  observation: "#94a3b8",
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  open: "#ef4444",
  draft: "#94a3b8",
  resolved: "#22c55e",
};

type Severity = "critical" | "major" | "minor" | "observation";
type TimeRange = "90d" | "30d" | "7d";

const SEVERITIES: Severity[] = ["critical", "major", "minor", "observation"];

// ============================================================
// TODO: REMOVE DUMMY DATA — only for design preview
// ============================================================
const USE_DUMMY_DATA = false;

type NewFinding = { _id: string; severity: string; title: string };
type TimeSeriesPoint = {
  date: string;
  critical: number;
  major: number;
  minor: number;
  observation: number;
  newFindings: NewFinding[];
};

function generateDummyTimeSeries(): TimeSeriesPoint[] {
  const data: TimeSeriesPoint[] = [];
  const now = new Date();
  const cum = { critical: 0, major: 0, minor: 0, observation: 0 };
  let findingCounter = 0;

  const titles: Record<string, string[]> = {
    critical: [
      "Fehlende Verschlüsselung",
      "Admin ohne MFA",
      "SQL Injection möglich",
      "Ungeschützte API-Endpunkte",
      "Root-Zugang ohne Audit",
    ],
    major: [
      "Veraltete TLS Konfiguration",
      "Fehlende Patch-Richtlinie",
      "Schwache Passwortregeln",
      "Backup nicht getestet",
      "Fehlende Netzwerksegmentierung",
    ],
    minor: [
      "Protokollierung unvollständig",
      "Schulungsnachweis fehlt",
      "Veraltete Firewall-Regeln",
      "Dokumentation veraltet",
      "Kein Change-Log",
    ],
    observation: [
      "Rechteüberprüfung ausstehend",
      "Kein Incident-Response-Plan",
      "Monitoring-Lücken",
      "Kein Pentest geplant",
    ],
  };

  for (let i = 90; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const newFindings: NewFinding[] = [];

    for (const sev of SEVERITIES) {
      const rate =
        sev === "critical"
          ? 0.12
          : sev === "major"
            ? 0.18
            : sev === "minor"
              ? 0.22
              : 0.15;
      if (Math.random() < rate) {
        cum[sev]++;
        findingCounter++;
        const titleList = titles[sev];
        newFindings.push({
          _id: `dummy-${findingCounter}`,
          severity: sev,
          title: titleList[findingCounter % titleList.length],
        });
      }
    }

    data.push({ date, ...cum, newFindings });
  }
  return data;
}

const DUMMY_TIME_SERIES = generateDummyTimeSeries();

// Build grouped dummy data from time series findings so IDs match
function buildDummyGrouped(timeSeries: TimeSeriesPoint[]) {
  const audits = [
    "ISO 27001 Surveillance",
    "SOC 2 Type II",
    "NIS 2 Compliance",
  ];
  const categories: Record<string, string[]> = {
    critical: ["A.8 – Kryptografie", "CC6 – Zugriffskontrolle"],
    major: ["Art. 21 – Risikomanagement", "A.12 – Betriebssicherheit"],
    minor: ["CC7 – Systemoperationen", "A.13 – Netzwerksicherheit"],
    observation: ["A.16 – Vorfallsmanagement", "A.7 – Personal"],
  };
  const statuses = ["open", "open", "open", "draft", "resolved"];

  const bySeverity: Record<
    string,
    {
      _id: string;
      title: string;
      status: string;
      auditId: string;
      auditName: string;
      categoryName: string;
      checklistItemId: string;
    }[]
  > = {
    critical: [],
    major: [],
    minor: [],
    observation: [],
  };

  for (const point of timeSeries) {
    for (const f of point.newFindings) {
      const cats = categories[f.severity] ?? ["Sonstiges"];
      bySeverity[f.severity]?.push({
        _id: f._id,
        title: f.title,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        auditId: `audit-${Math.floor(Math.random() * 3)}`,
        auditName: audits[Math.floor(Math.random() * audits.length)],
        categoryName: cats[Math.floor(Math.random() * cats.length)],
        checklistItemId: `checklist-item-${f._id}`,
      });
    }
  }

  const groups = SEVERITIES.map((sev) => {
    const findings = bySeverity[sev].slice(-10);
    const total = bySeverity[sev].length;
    return {
      severity: sev,
      total,
      open: findings.filter((f) => f.status === "open").length,
      draft: findings.filter((f) => f.status === "draft").length,
      resolved: findings.filter((f) => f.status === "resolved").length,
      findings,
    };
  });

  const totals = groups.reduce(
    (acc, g) => ({
      total: acc.total + g.total,
      open: acc.open + g.open,
      draft: acc.draft + g.draft,
      resolved: acc.resolved + g.resolved,
    }),
    { total: 0, open: 0, draft: 0, resolved: 0 },
  );

  return { groups, totals };
}

const DUMMY_GROUPED = buildDummyGrouped(DUMMY_TIME_SERIES);
// ============================================================

function FindingRow({
  finding,
  workspaceSlug,
  isHighlighted,
}: {
  finding: {
    _id: string;
    title: string;
    status: string;
    auditId: string;
    auditName: string;
    categoryName: string;
    checklistItemId: string;
  };
  workspaceSlug: string | undefined;
  isHighlighted?: boolean;
}) {
  const statusColor = STATUS_BADGE_COLORS[finding.status] ?? "#64748b";

  return (
    <div
      data-finding-id={finding._id}
      className={`flex items-center justify-between py-1.5 px-3 bg-surface rounded-lg mb-1 hover:bg-surface/80 transition-all ${isHighlighted ? "ring-2 ring-accent/50 bg-accent/5" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-primary truncate">
          {finding.title}
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-muted truncate">
            {finding.auditName}
          </span>
          {finding.categoryName && (
            <span className="text-[11px] font-medium px-1.5 rounded-md bg-card border border-border text-secondary whitespace-nowrap shrink-0">
              {finding.categoryName}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">
        <span
          className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
          style={{
            color: statusColor,
            background: `${statusColor}15`,
          }}
        >
          {finding.status}
        </span>
        {workspaceSlug && (
          <Link
            to="/$workspaceSlug/audits/$auditId"
            params={{
              workspaceSlug: workspaceSlug as string,
              auditId: finding.auditId,
            }}
            search={{
              tab: "checklist" as const,
              checklistItemId: finding.checklistItemId,
            }}
            className="text-muted hover:text-secondary transition-colors shrink-0"
            title="Go to finding"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="space-y-3">
      <div className="h-[250px] bg-surface rounded-lg animate-pulse" />
      <div className="flex items-center justify-center gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-surface animate-pulse" />
            <div className="w-16 h-3 bg-surface rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4 py-2 border-t border-border">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-16 h-4 bg-surface rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function FindingsSeverity() {
  const { t } = useTranslation(["dashboard"]);
  const { workspaceSlug } = useParams({ strict: false });

  const [activeOnly, setActiveOnly] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");
  const [chartType, setChartType] = useState<"line" | "bar">("bar");
  const [expandedSeverities, setExpandedSeverities] = useState<Set<string>>(
    () => new Set(),
  );
  const [highlightedFindingId, setHighlightedFindingId] = useState<
    string | null
  >(null);
  const [dotTooltip, setDotTooltip] = useState<{
    x: number;
    y: number;
    findings: NewFinding[];
    severity: Severity;
  } | null>(null);
  // Bar chart: selected date for drill-down
  const [selectedBarDate, setSelectedBarDate] = useState<string | null>(null);
  const chartWrapperRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const isCompact = expandedSeverities.size > 0;

  const { data: _timeSeriesData, isLoading: isLoadingTimeSeries } = useQuery({
    ...convexQuery(api.finding_queries.getTimeSeriesData, { activeOnly }),
  });

  const { data: _groupedData, isLoading: isLoadingGrouped } = useQuery({
    ...convexQuery(api.finding_queries.getBySeverityGrouped, { activeOnly }),
  });

  // TODO: REMOVE — swap back to real data after design review
  const timeSeriesData = USE_DUMMY_DATA ? DUMMY_TIME_SERIES : _timeSeriesData;
  const groupedData = USE_DUMMY_DATA ? DUMMY_GROUPED : _groupedData;
  const isLoading = USE_DUMMY_DATA
    ? false
    : isLoadingTimeSeries || isLoadingGrouped;

  const chartConfig: ChartConfig = useMemo(
    () => ({
      critical: {
        label: t("dashboard:findingsSeverity.severity.critical"),
        color: SEVERITY_COLORS.critical,
      },
      major: {
        label: t("dashboard:findingsSeverity.severity.major"),
        color: SEVERITY_COLORS.major,
      },
      minor: {
        label: t("dashboard:findingsSeverity.severity.minor"),
        color: SEVERITY_COLORS.minor,
      },
      observation: {
        label: t("dashboard:findingsSeverity.severity.observation"),
        color: SEVERITY_COLORS.observation,
      },
    }),
    [t],
  );

  const filteredData = useMemo(() => {
    if (!timeSeriesData?.length) return [];

    const now = new Date();
    let daysToSubtract = 90;
    if (timeRange === "30d") daysToSubtract = 30;
    else if (timeRange === "7d") daysToSubtract = 7;

    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - daysToSubtract);

    return timeSeriesData.filter((item) => new Date(item.date) >= startDate);
  }, [timeSeriesData, timeRange]);

  // Convert cumulative data to daily deltas for bar chart
  const barData = useMemo(() => {
    return filteredData.map((point, i) => {
      const prev = i > 0 ? filteredData[i - 1] : null;
      return {
        date: point.date,
        critical: prev ? point.critical - prev.critical : point.critical,
        major: prev ? point.major - prev.major : point.major,
        minor: prev ? point.minor - prev.minor : point.minor,
        observation: prev
          ? point.observation - prev.observation
          : point.observation,
        newFindings: point.newFindings,
      };
    });
  }, [filteredData]);

  // Only render Bar components for severities that have at least one non-zero day
  const activBarSeverities = useMemo(() => {
    return SEVERITIES.filter((sev) => barData.some((d) => d[sev] > 0));
  }, [barData]);

  // Collect finding IDs that fall within the selected time range
  const timeRangeFindingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const point of filteredData) {
      for (const f of point.newFindings) {
        ids.add(f._id);
      }
    }
    return ids;
  }, [filteredData]);

  // Filter grouped data to only include findings within the time range
  const filteredGroupedData = useMemo(() => {
    if (!groupedData) return groupedData;

    const groups = groupedData.groups.map((g) => {
      const findings = g.findings.filter((f) => timeRangeFindingIds.has(f._id));
      return {
        ...g,
        total: findings.length,
        open: findings.filter((f) => f.status === "open").length,
        draft: findings.filter((f) => f.status === "draft").length,
        resolved: findings.filter((f) => f.status === "resolved").length,
        findings,
      };
    });

    const totals = groups.reduce(
      (acc, g) => ({
        total: acc.total + g.total,
        open: acc.open + g.open,
        draft: acc.draft + g.draft,
        resolved: acc.resolved + g.resolved,
      }),
      { total: 0, open: 0, draft: 0, resolved: 0 },
    );

    return { groups, totals };
  }, [groupedData, timeRangeFindingIds]);

  const toggleSeverity = useCallback(
    (severity: string) => {
      // In bar mode, legend click shows all dates for that severity — clear date selection
      if (chartType === "bar") {
        setSelectedBarDate(null);
      }
      setExpandedSeverities((prev) => {
        const next = new Set(prev);
        if (next.has(severity)) {
          next.delete(severity);
        } else {
          next.add(severity);
        }
        return next;
      });
    },
    [chartType],
  );

  const scrollToFinding = useCallback((findingId: string) => {
    // Wait for detail list to render/animate, then scroll
    const attemptScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-finding-id="${findingId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };
    // Use a timeout to allow the expand animation to start rendering
    setTimeout(attemptScroll, 100);
  }, []);

  const handleDotClick = useCallback(
    (severity: Severity, findingId: string) => {
      setHighlightedFindingId((prev) =>
        prev === findingId ? null : findingId,
      );
      setExpandedSeverities((prev) => {
        const next = new Set(prev);
        next.add(severity);
        return next;
      });
      scrollToFinding(findingId);
    },
    [scrollToFinding],
  );

  // Bar chart: findings for the selected date + severity filter
  const barSelectedFindings = useMemo(() => {
    if (chartType !== "bar") return null;
    if (!filteredGroupedData) return null;

    // Mode 1: specific date selected (via date label or bar click)
    if (selectedBarDate) {
      const point = barData.find((p) => p.date === selectedBarDate);
      if (!point) return null;
      const dayFindingIds = new Set(point.newFindings.map((f) => f._id));
      if (dayFindingIds.size === 0) return null;

      // Filter to expanded severities only (if a specific bar was clicked, only that severity is expanded)
      const groups = filteredGroupedData.groups
        .filter((g) => expandedSeverities.has(g.severity))
        .map((g) => ({
          ...g,
          findings: g.findings.filter((f) => dayFindingIds.has(f._id)),
        }))
        .filter((g) => g.findings.length > 0);

      return groups.length > 0 ? groups : null;
    }

    // Mode 2: legend click (no specific date, show all findings for expanded severities)
    if (expandedSeverities.size > 0) {
      return filteredGroupedData.groups.filter(
        (g) => expandedSeverities.has(g.severity) && g.findings.length > 0,
      );
    }

    return null;
  }, [
    selectedBarDate,
    chartType,
    barData,
    filteredGroupedData,
    expandedSeverities,
  ]);

  // Click on date label → select all severities for that day
  const handleBarDateClick = useCallback(
    (date: string) => {
      setSelectedBarDate((prev) => {
        if (prev === date) {
          setExpandedSeverities(new Set());
          return null;
        }
        const point = barData.find((p) => p.date === date);
        if (point) {
          const sevsWithData = SEVERITIES.filter((sev) =>
            point.newFindings.some((f) => f.severity === sev),
          );
          setExpandedSeverities(new Set(sevsWithData));
        }
        return date;
      });
    },
    [barData],
  );

  // Click on individual bar → select that severity for that day
  const handleSingleBarClick = useCallback(
    (sev: Severity, entry: { date: string }) => {
      const isSame =
        selectedBarDate === entry.date &&
        expandedSeverities.has(sev) &&
        expandedSeverities.size === 1;
      if (isSame) {
        setSelectedBarDate(null);
        setExpandedSeverities(new Set());
      } else {
        setSelectedBarDate(entry.date);
        setExpandedSeverities(new Set([sev]));
      }
    },
    [selectedBarDate, expandedSeverities],
  );

  const isEmpty = filteredGroupedData && filteredGroupedData.totals.total === 0;

  const activeDetailGroups = filteredGroupedData
    ? SEVERITIES.filter((sev) => expandedSeverities.has(sev))
    : [];

  return (
    <div className="rounded-xl bg-card border border-border h-full flex flex-col">
      {/* Header — pinned */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:findingsSeverity.title")}
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-surface border border-border rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => {
                setChartType("line");
                setSelectedBarDate(null);
              }}
              className={`p-1 rounded-md transition-colors ${chartType === "line" ? "bg-card text-primary shadow-sm" : "text-muted hover:text-secondary"}`}
              title="Line chart"
            >
              <TrendingUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setChartType("bar")}
              className={`p-1 rounded-md transition-colors ${chartType === "bar" ? "bg-card text-primary shadow-sm" : "text-muted hover:text-secondary"}`}
              title="Bar chart"
            >
              <BarChart3 className="h-3.5 w-3.5" />
            </button>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            className="text-xs font-medium text-secondary bg-surface border border-border rounded-lg px-2 py-1 cursor-pointer hover:text-primary transition-colors focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="90d">
              {t("dashboard:findingsSeverity.timeRange.90d")}
            </option>
            <option value="30d">
              {t("dashboard:findingsSeverity.timeRange.30d")}
            </option>
            <option value="7d">
              {t("dashboard:findingsSeverity.timeRange.7d")}
            </option>
          </select>
          <select
            value={activeOnly ? "active" : "all"}
            onChange={(e) => setActiveOnly(e.target.value === "active")}
            className="text-xs font-medium text-secondary bg-surface border border-border rounded-lg px-2 py-1 cursor-pointer hover:text-primary transition-colors focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="active">
              {t("dashboard:findingsSeverity.scope.active")}
            </option>
            <option value="all">
              {t("dashboard:findingsSeverity.scope.all")}
            </option>
          </select>
        </div>
      </div>

      {/* Chart + Legend — fixed size */}
      <div className="shrink-0 px-4">
        {isLoading ? (
          <SkeletonChart />
        ) : isEmpty ? (
          <>
            <div className="py-12 text-center text-muted text-sm">
              {t("dashboard:findingsSeverity.empty")}
            </div>
            <SeverityLegend
              compact={false}
              chartConfig={chartConfig}
              expandedSeverities={expandedSeverities}
              onToggle={toggleSeverity}
              groupedData={filteredGroupedData}
            />
          </>
        ) : (
          <>
            {/* Chart + Legend flex container */}
            <div
              className={`flex ${
                isCompact
                  ? "flex-row items-end gap-6"
                  : "flex-col items-center justify-between"
              }`}
            >
              {/* Area Chart wrapper with CSS transition */}
              <div
                ref={chartWrapperRef}
                role="img"
                className={`relative transition-all duration-300 ease-in-out shrink-0 ${
                  isCompact ? "w-3/4 h-[130px]" : "w-full h-[220px]"
                }`}
                onMouseLeave={() => setDotTooltip(null)}
              >
                <ChartContainer
                  config={chartConfig}
                  className="h-full w-full bar-chart-interactive"
                >
                  {chartType === "bar" ? (
                    <BarChart data={barData}>
                      <defs>
                        <style>{`
                          .bar-chart-interactive .recharts-bar-rectangle {
                            transition: filter 0.15s ease, transform 0.15s ease;
                            transform-origin: bottom;
                          }
                          .bar-chart-interactive .recharts-bar-rectangle:hover {
                            filter: brightness(1.15);
                            transform: scaleY(1.04);
                          }
                          .bar-chart-interactive .recharts-xAxis text {
                            transition: fill 0.15s ease;
                          }
                          .bar-chart-interactive .recharts-xAxis text:hover {
                            fill: var(--color-primary) !important;
                          }
                        `}</style>
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        className="stroke-border"
                      />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={isCompact ? 4 : 8}
                        minTickGap={isCompact ? 60 : 32}
                        className="text-muted"
                        tick={(props: {
                          x: number;
                          y: number;
                          payload: { value: string };
                        }) => {
                          const { x, y, payload } = props;
                          const label = new Date(
                            payload.value,
                          ).toLocaleDateString("de-DE", {
                            month: "short",
                            day: "numeric",
                          });
                          const isSelected = selectedBarDate === payload.value;
                          return (
                            // biome-ignore lint/a11y/noStaticElementInteractions: SVG <text> used as clickable axis tick
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              dominantBaseline="hanging"
                              fontSize={isCompact ? 9 : 11}
                              fill={
                                isSelected
                                  ? "var(--color-primary)"
                                  : "var(--color-muted)"
                              }
                              fontWeight={isSelected ? 600 : 400}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleBarDateClick(payload.value)}
                            >
                              {label}
                            </text>
                          );
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={isCompact ? 24 : 32}
                        className="text-muted"
                        tick={{ fontSize: isCompact ? 9 : 11 }}
                        allowDecimals={false}
                      />
                      {!isCompact && (
                        <ChartTooltip
                          config={chartConfig}
                          labelFormatter={(value: string) =>
                            new Date(value).toLocaleDateString("de-DE", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          }
                        />
                      )}
                      {activBarSeverities.map((sev) => (
                        <Bar
                          key={sev}
                          dataKey={sev}
                          fill={SEVERITY_COLORS[sev]}
                          radius={[4, 4, 0, 0]}
                          hide={isCompact && !expandedSeverities.has(sev)}
                          cursor="pointer"
                          onClick={(_data: unknown, index: number) => {
                            const entry = barData[index];
                            if (entry) handleSingleBarClick(sev, entry);
                          }}
                        >
                          {barData.map((entry) => {
                            const isDateSelected =
                              selectedBarDate === entry.date;
                            const isSevSelected = expandedSeverities.has(sev);
                            const nothingSelected =
                              selectedBarDate === null &&
                              expandedSeverities.size === 0;
                            const isActive =
                              nothingSelected ||
                              (isDateSelected && isSevSelected) ||
                              (!selectedBarDate && isSevSelected);
                            return (
                              <Cell
                                key={entry.date}
                                opacity={isActive ? 1 : 0.3}
                                style={{
                                  transform:
                                    isDateSelected && isSevSelected
                                      ? "scaleY(1.06)"
                                      : "scaleY(1)",
                                  transformOrigin: "bottom",
                                  transition:
                                    "transform 0.2s ease, opacity 0.2s ease",
                                }}
                              />
                            );
                          })}
                        </Bar>
                      ))}
                    </BarChart>
                  ) : (
                    <AreaChart data={filteredData}>
                      <defs>
                        {SEVERITIES.map((sev) => (
                          <linearGradient
                            key={sev}
                            id={`fill-${sev}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={SEVERITY_COLORS[sev]}
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor={SEVERITY_COLORS[sev]}
                              stopOpacity={0.02}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        className="stroke-border"
                      />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={isCompact ? 4 : 8}
                        minTickGap={isCompact ? 60 : 32}
                        className="text-muted"
                        tick={{ fontSize: isCompact ? 9 : 11 }}
                        tickFormatter={(value: string) => {
                          const date = new Date(value);
                          return date.toLocaleDateString("de-DE", {
                            month: "short",
                            day: "numeric",
                          });
                        }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={isCompact ? 24 : 32}
                        className="text-muted"
                        tick={{ fontSize: isCompact ? 9 : 11 }}
                        allowDecimals={false}
                      />
                      {!isCompact && (
                        <ChartTooltip
                          config={chartConfig}
                          labelFormatter={(value: string) =>
                            new Date(value).toLocaleDateString("de-DE", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          }
                        />
                      )}
                      {SEVERITIES.map((sev) => (
                        <Area
                          key={sev}
                          dataKey={sev}
                          type="natural"
                          fill={`url(#fill-${sev})`}
                          stroke={SEVERITY_COLORS[sev]}
                          strokeWidth={isCompact ? 2 : 2.5}
                          dot={false}
                          activeDot={false}
                          hide={isCompact && !expandedSeverities.has(sev)}
                        />
                      ))}
                      {/* Dots overlay */}
                      <Customized
                        component={(props: Record<string, unknown>) => {
                          const items = props.formattedGraphicalItems as
                            | Array<{
                                props: {
                                  points: Array<{ x: number; y: number }>;
                                };
                                item: { props: { dataKey: string } };
                              }>
                            | undefined;
                          if (!items) return <g />;

                          return (
                            <g className="dots-overlay">
                              <style>{`
                                .finding-dot .dot-visible {
                                  transition: transform 0.2s ease, opacity 0.15s ease;
                                }
                                .finding-dot:hover .dot-visible {
                                  transform: scale(2);
                                  opacity: 1 !important;
                                }
                                .finding-dot .dot-glow {
                                  opacity: 0;
                                  transition: opacity 0.2s ease;
                                }
                                .finding-dot:hover .dot-glow {
                                  opacity: 0.25;
                                }
                              `}</style>
                              {items.map((areaItem) => {
                                const sev = areaItem.item.props
                                  .dataKey as Severity;
                                if (!SEVERITIES.includes(sev)) return null;
                                if (isCompact && !expandedSeverities.has(sev))
                                  return null;
                                return areaItem.props.points.map(
                                  (point, idx) => {
                                    const dataPoint = filteredData[idx];
                                    if (!dataPoint) return null;
                                    const newForSev =
                                      dataPoint.newFindings?.filter(
                                        (f) => f.severity === sev,
                                      );
                                    if (!newForSev?.length) return null;
                                    const isHighlighted = newForSev.some(
                                      (f) => f._id === highlightedFindingId,
                                    );
                                    return (
                                      // biome-ignore lint/a11y/noStaticElementInteractions: SVG <g> cannot be a semantic element
                                      <g
                                        key={`dot-${sev}-${dataPoint.date}`}
                                        className="finding-dot"
                                        onClick={() =>
                                          handleDotClick(sev, newForSev[0]._id)
                                        }
                                        onMouseEnter={() =>
                                          setDotTooltip({
                                            x: point.x,
                                            y: point.y,
                                            findings: newForSev,
                                            severity: sev,
                                          })
                                        }
                                        onMouseLeave={() => setDotTooltip(null)}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r={12}
                                          fill="transparent"
                                        />
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r={8}
                                          fill={SEVERITY_COLORS[sev]}
                                          className="dot-glow"
                                          style={{
                                            transformOrigin: `${point.x}px ${point.y}px`,
                                          }}
                                        />
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r={isHighlighted ? 5 : 2.5}
                                          fill={SEVERITY_COLORS[sev]}
                                          stroke="var(--color-card, #fff)"
                                          strokeWidth={1.5}
                                          opacity={0.8}
                                          className="dot-visible"
                                          style={{
                                            transformOrigin: `${point.x}px ${point.y}px`,
                                          }}
                                        />
                                        {isHighlighted && (
                                          <circle
                                            cx={point.x}
                                            cy={point.y}
                                            r={8}
                                            fill="none"
                                            stroke={SEVERITY_COLORS[sev]}
                                            strokeWidth={1.5}
                                            opacity={0.4}
                                          />
                                        )}
                                      </g>
                                    );
                                  },
                                );
                              })}
                            </g>
                          );
                        }}
                      />
                    </AreaChart>
                  )}
                </ChartContainer>

                {/* Dot hover tooltip — line mode only */}
                {chartType === "line" && dotTooltip && (
                  <div
                    className="absolute z-10 pointer-events-none rounded-lg border border-border bg-card/95 backdrop-blur-sm px-2.5 py-1.5 shadow-sm transition-opacity"
                    style={{
                      left: dotTooltip.x,
                      top: dotTooltip.y - 8,
                      transform: "translate(-50%, -100%)",
                    }}
                  >
                    {dotTooltip.findings.map((f) => (
                      <div key={f._id} className="flex items-center gap-1.5">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{
                            background: SEVERITY_COLORS[dotTooltip.severity],
                          }}
                        />
                        <span className="text-[11px] text-primary whitespace-nowrap max-w-[180px] truncate">
                          {f.title}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Legend — horizontal below (full) or vertical right (compact) */}
              <div className={isCompact ? "pb-6" : "w-full shrink-0"}>
                <SeverityLegend
                  compact={isCompact}
                  chartConfig={chartConfig}
                  expandedSeverities={expandedSeverities}
                  onToggle={toggleSeverity}
                  groupedData={filteredGroupedData}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Scrollable detail list — only takes space when content exists */}
      <div
        ref={scrollContainerRef}
        className={`overflow-y-auto min-h-0 px-4 pb-1 ${activeDetailGroups.length > 0 ? "flex-1" : ""}`}
      >
        <AnimatePresence>
          {activeDetailGroups.length > 0 && filteredGroupedData && (
            <motion.div
              key="detail"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="border-t border-border pt-3 overflow-hidden"
            >
              {/* Date header for bar mode */}
              {chartType === "bar" && selectedBarDate && (
                <div className="flex items-center justify-between pb-2">
                  <span className="text-xs font-semibold text-secondary">
                    {new Date(selectedBarDate).toLocaleDateString("de-DE", {
                      weekday: "short",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBarDate(null);
                      setExpandedSeverities(new Set());
                    }}
                    className="text-xs text-muted hover:text-secondary transition-colors"
                  >
                    &times;
                  </button>
                </div>
              )}
              {activeDetailGroups.map((sev) => {
                // In bar mode, use barSelectedFindings (handles both date-specific and legend-click)
                const group =
                  chartType === "bar"
                    ? barSelectedFindings?.find((g) => g.severity === sev)
                    : filteredGroupedData.groups.find(
                        (g) => g.severity === sev,
                      );
                if (!group) return null;
                const findings = group.findings;
                return (
                  <div key={sev}>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary pb-1.5">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: SEVERITY_COLORS[sev] }}
                      />
                      {chartConfig[sev].label} ({findings.length})
                    </div>
                    {findings.length === 0 ? (
                      <div className="text-xs text-muted py-2 pl-4">
                        {t("dashboard:findingsSeverity.empty")}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1 mb-3">
                        {findings.map((finding) => (
                          <FindingRow
                            key={finding._id}
                            finding={finding}
                            workspaceSlug={workspaceSlug}
                            isHighlighted={finding._id === highlightedFindingId}
                          />
                        ))}
                        {group.total > 10 && (
                          <div className="text-center pt-1">
                            <span className="text-xs text-accent font-medium cursor-pointer hover:underline">
                              {t("dashboard:findingsSeverity.showAll")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Summary Strip — pinned at bottom */}
      {filteredGroupedData && (
        <motion.div layout transition={{ duration: 0.25, ease: "easeInOut" }}>
          <SummaryStrip totals={filteredGroupedData.totals} />
        </motion.div>
      )}
    </div>
  );
}

function SeverityLegend({
  compact,
  chartConfig,
  expandedSeverities,
  onToggle,
  groupedData,
}: {
  compact: boolean;
  chartConfig: ChartConfig;
  expandedSeverities: Set<string>;
  onToggle: (severity: string) => void;
  groupedData?: {
    groups: { severity: string; total: number }[];
  };
}) {
  return (
    <div
      className={
        compact
          ? "flex flex-col gap-1"
          : "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-1"
      }
    >
      {SEVERITIES.map((sev) => {
        const isActive = expandedSeverities.has(sev);
        const count = groupedData?.groups.find(
          (g) => g.severity === sev,
        )?.total;
        return (
          <button
            key={sev}
            type="button"
            onClick={() => onToggle(sev)}
            className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity outline-none"
          >
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: SEVERITY_COLORS[sev] }}
            />
            <span
              className={`text-xs whitespace-nowrap transition-colors ${
                isActive ? "text-primary font-semibold" : "text-secondary"
              }`}
            >
              {chartConfig[sev].label}
              {count !== undefined ? ` (${count})` : ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SummaryStrip({
  totals,
}: {
  totals: { total: number; open: number; draft: number; resolved: number };
}) {
  const { t } = useTranslation(["dashboard"]);
  return (
    <div className="flex items-center justify-center gap-4 py-1.5 border-t border-border mx-4 shrink-0 text-xs">
      <span className="font-semibold text-primary">
        {totals.total} {t("dashboard:findingsSeverity.total")}
      </span>
      <span className="text-red-500 font-medium">
        {totals.open} {t("dashboard:findingsSeverity.status.open")}
      </span>
      <span className="text-slate-400 font-medium">
        {totals.draft} {t("dashboard:findingsSeverity.status.draft")}
      </span>
      <span className="text-green-500 font-medium">
        {totals.resolved} {t("dashboard:findingsSeverity.status.resolved")}
      </span>
    </div>
  );
}
